import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";

import type { AppRouter } from "@runcheetah/api/router";
import { WORKSPACE_BILLING_RECHECKOUT_STATUSES } from "@runcheetah/shared";
import { CodeText, InfoTile, PageHeader, QueryErrorState, Select } from "@/components/common";
import {
  WORKSPACE_BILLING_STATUS_OPTIONS,
  getWorkspaceBillingStatusMeta,
} from "@/lib/billing-status";
import { formatDateTime } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Skeleton } from "@runcheetah/ui";

type RouterInputs = inferRouterInputs<AppRouter>;
type RouterOutputs = inferRouterOutputs<AppRouter>;
type WorkspaceDetail = RouterOutputs["admin"]["workspaces"]["get"];
type WorkspaceBillingPlan = RouterOutputs["admin"]["workspaces"]["billingPlans"][number];
type WorkspaceBillingStatus = NonNullable<WorkspaceDetail["billingAccount"]>["status"];
type AssignWorkspacePlanInput = RouterInputs["admin"]["workspaces"]["assignPlan"];
type RevokeWorkspaceSubscriptionInput = RouterInputs["admin"]["workspaces"]["revokeSubscription"];
type ResetWorkspaceCreditsInput = RouterInputs["admin"]["workspaces"]["resetCredits"];

const formatDate = formatDateTime;

const BRAND_STATUS_LABELS: Record<WorkspaceDetail["brands"][number]["status"], string> = {
  queued: "Queued",
  processing: "Processing",
  scanned: "Scanned",
  failed: "Failed",
};

function formatWorkspaceCredits(value: number): string {
  return `${value.toLocaleString()} ${value === 1 ? "credit" : "credits"}`;
}

function formatCreditUsage(workspace: WorkspaceDetail): string {
  const used = workspace.billingAccount?.currentPeriodCreditUsed ?? 0;
  return `${formatWorkspaceCredits(used)} used`;
}

function BillingStatusPill({ status }: { status: WorkspaceDetail["billingAccount"] }) {
  if (!status) {
    return (
      <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">
        Unassigned
      </span>
    );
  }

  const statusMeta = getWorkspaceBillingStatusMeta(status.status);

  return (
    <span
      className={cn(
        "inline-flex rounded-full border px-2 py-0.5 text-xs font-medium",
        statusMeta.className,
      )}
    >
      {statusMeta.label}
    </span>
  );
}

function WorkspaceSummaryCards({ workspace }: { workspace: WorkspaceDetail }) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
      <InfoTile label="Current plan" value={workspace.plan?.name ?? "No plan"} />
      <InfoTile label="Billing status">
        <div className="mt-1">
          <BillingStatusPill status={workspace.billingAccount} />
        </div>
      </InfoTile>
      <InfoTile
        label="Billing cycle"
        value={
          workspace.billingAccount
            ? workspace.billingAccount.billingCycle === "monthly"
              ? "Monthly"
              : "Yearly"
            : "Not set"
        }
      />
      <InfoTile
        label="Coverage"
        value={`${workspace.members.length} members · ${workspace.brands.length} brands`}
      />
      <InfoTile label="Credits" value={formatCreditUsage(workspace)} />
    </div>
  );
}

function WorkspaceOverviewCard({ workspace }: { workspace: WorkspaceDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Workspace summary</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <InfoTile label="Owner">
          <div className="mt-1 space-y-1">
            <Link
              to={`/users/${workspace.ownerUserId}`}
              className="block font-medium hover:underline"
            >
              {workspace.ownerName ?? workspace.ownerEmail}
            </Link>
            <div className="text-sm text-muted-foreground">{workspace.ownerEmail}</div>
          </div>
        </InfoTile>
        <InfoTile label="Created" value={formatDate(workspace.createdAt)} />
        <InfoTile label="Last updated" value={formatDate(workspace.updatedAt)} />
        <InfoTile label="Workspace ID">
          <CodeText className="mt-1 block">{workspace.id}</CodeText>
        </InfoTile>
      </CardContent>
    </Card>
  );
}

function getInitialPlanId(
  workspace: WorkspaceDetail,
  billingPlans: WorkspaceBillingPlan[],
): string {
  return workspace.billingAccount?.planId ?? billingPlans[0]?.id ?? "";
}

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function addBillingCycleToDate(
  value: string,
  billingCycle: AssignWorkspacePlanInput["billingCycle"],
): string {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return "";
  }

  const date = new Date(year, month - 1, day);
  if (billingCycle === "yearly") {
    date.setFullYear(date.getFullYear() + 1);
  } else {
    date.setMonth(date.getMonth() + 1);
  }

  return formatDateInputValue(date);
}

function getInitialPeriodStart(workspace: WorkspaceDetail): string {
  return (
    workspace.billingAccount?.currentPeriodStart?.slice(0, 10) ?? formatDateInputValue(new Date())
  );
}

function getInitialPeriodEnd(
  workspace: WorkspaceDetail,
  billingCycle: AssignWorkspacePlanInput["billingCycle"],
): string {
  return (
    workspace.billingAccount?.currentPeriodEnd?.slice(0, 10) ??
    addBillingCycleToDate(getInitialPeriodStart(workspace), billingCycle)
  );
}

function hasLiveStripeSubscription(workspace: WorkspaceDetail): boolean {
  const account = workspace.billingAccount;
  return Boolean(
    account?.hasStripeSubscription &&
    !WORKSPACE_BILLING_RECHECKOUT_STATUSES.includes(account.status),
  );
}

function AssignPlanCard({
  workspace,
  billingPlans,
  isPending,
  isRevoking,
  isResettingCredits,
  errorMessage,
  onApply,
  onRevoke,
  onResetCredits,
}: {
  workspace: WorkspaceDetail;
  billingPlans: WorkspaceBillingPlan[];
  isPending: boolean;
  isRevoking: boolean;
  isResettingCredits: boolean;
  errorMessage: string | null;
  onApply: (input: AssignWorkspacePlanInput) => void;
  onRevoke: (input: RevokeWorkspaceSubscriptionInput) => void;
  onResetCredits: (input: ResetWorkspaceCreditsInput) => void;
}) {
  const [planId, setPlanId] = useState(() => getInitialPlanId(workspace, billingPlans));
  const [billingCycle, setBillingCycle] = useState<AssignWorkspacePlanInput["billingCycle"]>(
    workspace.billingAccount?.billingCycle ?? "monthly",
  );
  const [status, setStatus] = useState<WorkspaceBillingStatus>(
    workspace.billingAccount?.status ?? "active",
  );
  const [currentPeriodStart, setCurrentPeriodStart] = useState(() =>
    getInitialPeriodStart(workspace),
  );
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(() =>
    getInitialPeriodEnd(workspace, workspace.billingAccount?.billingCycle ?? "monthly"),
  );
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(
    workspace.billingAccount?.cancelAtPeriodEnd ?? false,
  );

  useEffect(() => {
    setPlanId(getInitialPlanId(workspace, billingPlans));
    const nextBillingCycle = workspace.billingAccount?.billingCycle ?? "monthly";
    setBillingCycle(nextBillingCycle);
    setStatus(workspace.billingAccount?.status ?? "active");
    setCurrentPeriodStart(getInitialPeriodStart(workspace));
    setCurrentPeriodEnd(getInitialPeriodEnd(workspace, nextBillingCycle));
    setCancelAtPeriodEnd(workspace.billingAccount?.cancelAtPeriodEnd ?? false);
  }, [workspace, billingPlans]);

  const handleBillingCycleChange = (nextBillingCycle: AssignWorkspacePlanInput["billingCycle"]) => {
    setBillingCycle(nextBillingCycle);
    setCurrentPeriodEnd(addBillingCycleToDate(currentPeriodStart, nextBillingCycle));
  };

  const handlePeriodStartChange = (nextPeriodStart: string) => {
    setCurrentPeriodStart(nextPeriodStart);
    setCurrentPeriodEnd(addBillingCycleToDate(nextPeriodStart, billingCycle));
  };

  const selectedPlan = billingPlans.find((plan) => plan.id === planId);
  const mutationPending = isPending || isRevoking || isResettingCredits;
  const assignmentLocked = hasLiveStripeSubscription(workspace);
  const canSubmit = Boolean(selectedPlan) && !mutationPending && !assignmentLocked;
  const canRevoke = Boolean(workspace.billingAccount) && !mutationPending && !assignmentLocked;
  const canResetCredits = Boolean(workspace.billingAccount) && !mutationPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Assign plan</CardTitle>
        <p className="text-sm text-muted-foreground">
          {assignmentLocked
            ? "Stripe is the source of truth for this workspace. Cancel the subscription in Stripe before assigning or revoking local billing state."
            : "Set the beta plan, billing cadence, and subscription status the workspace should operate under."}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {assignmentLocked ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Manual plan assignment and local subscription revocation are disabled because this
            workspace has a live Stripe subscription. Cancel it in Stripe first.
          </div>
        ) : null}
        <div className="grid gap-3 md:grid-cols-2">
          <label className="space-y-2">
            <span className="text-sm font-medium">Plan</span>
            <Select
              value={planId}
              disabled={assignmentLocked}
              onChange={(event) => setPlanId(event.target.value)}
            >
              {billingPlans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">Billing cycle</span>
            <Select
              value={billingCycle}
              disabled={assignmentLocked}
              onChange={(event) =>
                handleBillingCycleChange(
                  event.target.value as AssignWorkspacePlanInput["billingCycle"],
                )
              }
            >
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </Select>
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">Status</span>
            <Select
              value={status}
              disabled={assignmentLocked}
              onChange={(event) => setStatus(event.target.value as WorkspaceBillingStatus)}
            >
              {WORKSPACE_BILLING_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">Cancel at period end</span>
            <Select
              value={cancelAtPeriodEnd ? "yes" : "no"}
              disabled={assignmentLocked}
              onChange={(event) => setCancelAtPeriodEnd(event.target.value === "yes")}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </Select>
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">Current period start</span>
            <Input
              type="date"
              value={currentPeriodStart}
              disabled={assignmentLocked}
              onChange={(event) => handlePeriodStartChange(event.target.value)}
            />
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">Current period end</span>
            <Input
              type="date"
              value={currentPeriodEnd}
              disabled={assignmentLocked}
              onChange={(event) => setCurrentPeriodEnd(event.target.value)}
            />
          </label>
        </div>

        <div className="rounded-2xl border bg-muted/30 p-3 text-sm text-muted-foreground">
          <div className="font-medium text-foreground">Selected plan snapshot</div>
          <div className="mt-2 flex flex-wrap gap-4">
            <span>Code: {selectedPlan?.code ?? "-"}</span>
            <span>
              Brand limit:{" "}
              {selectedPlan?.brandLimit === null ? "Unlimited" : selectedPlan?.brandLimit}
            </span>
            <span>
              Monthly allowance:{" "}
              {selectedPlan?.monthlyCreditAllowance == null
                ? "Custom"
                : formatWorkspaceCredits(selectedPlan.monthlyCreditAllowance)}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            disabled={!canSubmit}
            onClick={() =>
              onApply({
                workspaceId: workspace.id,
                planId,
                billingCycle,
                status,
                currentPeriodStart: currentPeriodStart
                  ? `${currentPeriodStart}T00:00:00.000Z`
                  : null,
                currentPeriodEnd: currentPeriodEnd ? `${currentPeriodEnd}T23:59:59.000Z` : null,
                cancelAtPeriodEnd,
              })
            }
          >
            {isPending ? "Saving..." : "Save plan assignment"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="border-red-800/25 bg-white! text-red-900 hover:bg-red-100!"
            disabled={!canRevoke}
            onClick={() => onRevoke({ workspaceId: workspace.id })}
          >
            {isRevoking ? "Revoking..." : "Revoke subscription"}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!canResetCredits}
            onClick={() => onResetCredits({ workspaceId: workspace.id })}
          >
            {isResettingCredits ? "Resetting credits..." : "Reset credits"}
          </Button>
          {errorMessage ? <span className="text-sm text-destructive">{errorMessage}</span> : null}
        </div>
      </CardContent>
    </Card>
  );
}

function WorkspaceMembersCard({ workspace }: { workspace: WorkspaceDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Members</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {workspace.members.map((member) => (
          <div key={member.userId} className="rounded-2xl border p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <Link to={`/users/${member.userId}`} className="font-medium hover:underline">
                  {member.name ?? member.email}
                </Link>
                <div className="text-sm text-muted-foreground">{member.email}</div>
              </div>
              <span className="rounded-full border px-2 py-0.5 text-xs font-medium capitalize text-muted-foreground">
                {member.role}
              </span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function WorkspaceBrandsCard({ workspace }: { workspace: WorkspaceDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Linked brands</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {workspace.brands.map((brand) => (
          <div key={brand.id} className="rounded-2xl border p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <Link to={`/brands/${brand.id}`} className="font-medium hover:underline">
                  {brand.sourceDomain}
                </Link>
                <div className="text-xs text-muted-foreground">{brand.id}</div>
              </div>
              <span className="rounded-full border px-2 py-0.5 text-xs font-medium">
                {BRAND_STATUS_LABELS[brand.status]}
              </span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function WorkspaceDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const utils = trpc.useUtils();

  const workspaceQuery = trpc.admin.workspaces.get.useQuery({ id: id! }, { enabled: !!id });
  const billingPlansQuery = trpc.admin.workspaces.billingPlans.useQuery();
  const assignPlanMutation = trpc.admin.workspaces.assignPlan.useMutation({
    onSuccess: async (workspace) => {
      utils.admin.workspaces.get.setData({ id: workspace.id }, workspace);
      await utils.admin.workspaces.list.invalidate();
    },
  });
  const revokeSubscriptionMutation = trpc.admin.workspaces.revokeSubscription.useMutation({
    onSuccess: async (workspace) => {
      utils.admin.workspaces.get.setData({ id: workspace.id }, workspace);
      await utils.admin.workspaces.list.invalidate();
    },
  });
  const resetCreditsMutation = trpc.admin.workspaces.resetCredits.useMutation({
    onSuccess: async (workspace) => {
      utils.admin.workspaces.get.setData({ id: workspace.id }, workspace);
      await utils.admin.workspaces.list.invalidate();
    },
  });

  if (!id) {
    return (
      <QueryErrorState
        title="Workspace not found"
        message="Choose a workspace from the workspace list."
      />
    );
  }

  if (workspaceQuery.isPending || billingPlansQuery.isPending) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Skeleton className="size-8" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const error = workspaceQuery.error ?? billingPlansQuery.error;
  if (error || !workspaceQuery.data) {
    return (
      <QueryErrorState
        title="Workspace not found"
        message={error?.message ?? "Choose a workspace from the workspace list."}
        isForbidden={error?.data?.code === "FORBIDDEN"}
      />
    );
  }

  const workspace = workspaceQuery.data;
  const billingPlans = billingPlansQuery.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={workspace.name}
        meta={workspace.plan?.name ?? "No plan"}
        description="Admin-only beta billing controls for assigning workspace plans before payments ship."
      />
      <WorkspaceSummaryCards workspace={workspace} />
      <WorkspaceOverviewCard workspace={workspace} />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <AssignPlanCard
          workspace={workspace}
          billingPlans={billingPlans}
          isPending={assignPlanMutation.isPending}
          isRevoking={revokeSubscriptionMutation.isPending}
          isResettingCredits={resetCreditsMutation.isPending}
          errorMessage={
            assignPlanMutation.error?.message ??
            revokeSubscriptionMutation.error?.message ??
            resetCreditsMutation.error?.message ??
            null
          }
          onApply={(input) => assignPlanMutation.mutate(input)}
          onRevoke={(input) => revokeSubscriptionMutation.mutate(input)}
          onResetCredits={(input) => resetCreditsMutation.mutate(input)}
        />
        <div className="space-y-6">
          <WorkspaceMembersCard workspace={workspace} />
          <WorkspaceBrandsCard workspace={workspace} />
        </div>
      </div>
    </div>
  );
}
